// Config experiments (iteration 18; replaces iteration 10's on/off `abTest`).
//
// A customer's config may be an EXPERIMENT (`defineExperiment`, ./define.ts): N complete
// configurations ("arms", `null` = no widgets) plus ONE shared `analytics`. boot() resolves
// the arm ONCE per page load here, then runs its normal flow on `{ ...arm, analytics }`, so
// every widget lands in the same arm and analytics runs on all of them (the null arm
// included: it has to measure the baseline). The variant id is the arm INDEX as a string
// ("0", "1", …) — what the analytics backend already reports on; by convention arm "0" is
// the baseline, what the shop runs today.
//
// Resolution order (resolveArm):
//   1. `?rvn_variant=<n>` on the page URL (QA + customer preview): a valid index wins for
//      THIS PAGE LOAD ONLY. Never written to storage, so the visitor's real assignment is
//      left as it was (and none is created when they had none). Invalid → console.warn,
//      ignored, normal resolution.
//   2. the sticky bucket in localStorage under `_rvn_ab:<id>` (value = arm index), reused
//      when it is a valid index for the CURRENT arm count. Anything else stored there
//      (garbage, an index out of range after arms were removed) counts as no assignment.
//   3. a fresh weighted pick (`weights`, default equal), written back to `_rvn_ab:<id>`.
// Storage that throws (blocked / private mode) → the pick lives in memory for this load
// only. The id is part of the key, so a new id re-buckets everyone: change it when an arm
// changes materially mid-run (results across experiments are only separable by id).
//
// A BROKEN experiment config (bad id / no arms / bad weights) throws from resolveArm; boot()
// records it as product "ab" and runs arm 0 unrecorded (variant null) — arm 0 is the
// baseline, the safest fallback. Broken STORAGE is never an error (see above).
//
// History: iteration 10 re-implemented the old per-SDK loader bucket (sdk-analytics'
// src/ab.ts `#include`d into the search / rce CDN loaders; the facade drives the SDKs'
// LIBRARY APIs, which carry no A/B code) as one on/off switch under the bare `_rvn_ab` key.
// Kept from it, deliberately: localStorage ONLY (no cookie read or write — no SDK reads the
// cookie) and ONE `RaventicABTest` exposure event per page (not one per widget). The bare
// `_rvn_ab` key is simply no longer read.
//
// Timing (iteration 20 boot head start): everything here runs at widget.js execution, before
// the DOM wait — none of it reads the host page. The arm is also published on <html>
// (markArm) so the customer's light CSS can scope host-element changes to the pages where
// widgets run (`[data-rvn-active] .native-thing { … }`) or to one arm
// (`[data-rvn-ab="1"] …`), and the exposure event is pushed at once; as a host script parsed
// after widget.js may still REPLACE `window.dataLayer` (`dataLayer = [{…}]`, the classic
// "declare above the GTM snippet" pattern) — or `window.raventicLayer` — keepAbEvent()
// re-pushes the same entry at DOM readiness when it is no longer in its layer.
//
// Import-free on purpose (only types): boot() runs it before the DOM wait, and the unit
// tests load it under plain Node.

import type { AnalyticsActivation, ExperimentConfig } from "./config";

// <html> attributes set at script start (markArm). Customer light CSS may hide / alter host
// elements ONLY under `[data-rvn-active]` (never set when the booted config has no widgets —
// a null arm; and only for host elements EVERY widget-bearing arm replaces) or under one
// arm's `[data-rvn-ab="<n>"]` (optionally with `[data-rvn-experiment="<id>"]`), see
// templates/customer/styles/light.scss.example.
//   data-rvn-ab="<n>"           — the recorded experiment arm (variant non-null);
//   data-rvn-experiment="<id>"  — the experiment marker: its id, set together with data-rvn-ab
//                                 on EVERY recorded arm (the null arm included), so it scopes
//                                 an experiment, not an arm;
//   data-rvn-active (empty)     — the booted config (a plain config, or the resolved arm) has
//                                 at least one actual widget (boot.ts hasWidgets).
export const AB_ATTRIBUTE = "data-rvn-ab";
export const EXPERIMENT_ATTRIBUTE = "data-rvn-experiment";
export const ACTIVE_ATTRIBUTE = "data-rvn-active";

// localStorage key of experiment `id`'s sticky arm: `_rvn_ab:<id>`.
export const AB_STORAGE_PREFIX = "_rvn_ab:";
// Page-URL parameter forcing an arm for one page load (`?rvn_variant=1`).
export const AB_OVERRIDE_PARAM = "rvn_variant";
// The id goes into the storage key and the exposure event: a short lowercase slug.
export const EXPERIMENT_ID_PATTERN = /^[a-z0-9][a-z0-9-]{0,39}$/;

const AB_EVENT_NAME = "RaventicABTest";
// A canonical non-negative decimal ("0", "12"; not "01", "1.0", " 1", "-0").
const INDEX_PATTERN = /^(0|[1-9][0-9]*)$/;

// Narrow local view of the globals touched here (the analytics package's own Window
// augmentation types `raventicVariant` as `string | null`; this view is compatible).
interface AbWindow {
  dataLayer?: unknown[];
  raventicLayer?: unknown[];
  raventicVariant?: string | null;
}

// The storage slice resolveArm needs (injectable for tests).
export type ArmStorage = Pick<Storage, "getItem" | "setItem">;

export interface ArmDeps {
  // Uniform [0, 1); default Math.random.
  random?: () => number;
  // Default `window.localStorage`, read lazily inside a try (merely touching
  // `window.localStorage` throws in some blocked-storage modes).
  storage?: ArmStorage;
}

export interface ResolvedArm {
  // Index into `experiment.arms`.
  index: number;
  // The variant id reported everywhere: String(index).
  variant: string;
  // Where it came from: the URL override (not stored), a valid stored assignment, or a
  // fresh weighted pick (stored, or in memory when storage throws).
  source: "override" | "stored" | "assigned";
}

// Throws (with a message naming the problem) when the experiment config itself is broken:
// id not matching EXPERIMENT_ID_PATTERN, no arms, or weights not one positive finite number
// per arm. Runtime check on top of the types — a config is plain JS at the shop.
export function validateExperiment(experiment: ExperimentConfig): void {
  const { id, arms, weights } = experiment;
  if (typeof id !== "string" || !EXPERIMENT_ID_PATTERN.test(id)) {
    throw new Error(
      `[@raventic/sdk] experiment: invalid id ${JSON.stringify(id)} (expected ${String(EXPERIMENT_ID_PATTERN)})`,
    );
  }
  if (!Array.isArray(arms) || arms.length === 0) {
    throw new Error(`[@raventic/sdk] experiment "${id}": no arms`);
  }
  if (weights !== undefined) {
    if (!Array.isArray(weights) || weights.length !== arms.length) {
      throw new Error(`[@raventic/sdk] experiment "${id}": weights must have one entry per arm (${arms.length})`);
    }
    if (!weights.every((w) => typeof w === "number" && Number.isFinite(w) && w > 0)) {
      throw new Error(`[@raventic/sdk] experiment "${id}": every weight must be a positive number`);
    }
    // Finite one by one can still overflow in sum ([1e308, 1e308] → Infinity), which would put
    // every visitor in the last arm.
    if (!Number.isFinite(weights.reduce((sum, w) => sum + w, 0))) {
      throw new Error(`[@raventic/sdk] experiment "${id}": the weights' sum must be finite`);
    }
  }
}

// Resolve this page load's arm: URL override → valid stored `_rvn_ab:<id>` → weighted pick
// (stored). Throws only for a broken experiment config (validateExperiment); storage errors
// are swallowed. `search` defaults to the page's `location.search`.
export function resolveArm(
  experiment: ExperimentConfig,
  search: string = currentSearch(),
  deps: ArmDeps = {},
): ResolvedArm {
  validateExperiment(experiment);
  const count = experiment.arms.length;

  // 1. One-load override. Never stored: a QA / preview link must not re-bucket the visitor.
  const forced = new URLSearchParams(search).get(AB_OVERRIDE_PARAM);
  if (forced !== null) {
    const index = parseIndex(forced, count);
    if (index !== null) return { index, variant: String(index), source: "override" };
    console.warn(
      `[@raventic/sdk] ignoring ?${AB_OVERRIDE_PARAM}=${forced}: experiment "${experiment.id}" has arms 0..${count - 1}`,
    );
  }

  // 2. Sticky assignment, if it is still a valid index (arms may have been removed).
  const key = AB_STORAGE_PREFIX + experiment.id;
  let stored: string | null = null;
  try {
    stored = (deps.storage ?? window.localStorage).getItem(key);
  } catch {
    // Unreadable storage: fall through to a fresh in-memory pick.
  }
  const storedIndex = stored === null ? null : parseIndex(stored, count);
  if (storedIndex !== null) return { index: storedIndex, variant: String(storedIndex), source: "stored" };

  // 3. Fresh weighted pick, overwriting whatever invalid value was there.
  const index = pickWeighted(experiment.weights ?? experiment.arms.map(() => 1), deps.random ?? Math.random);
  try {
    (deps.storage ?? window.localStorage).setItem(key, String(index));
  } catch {
    // Ignore storage errors; continue with the in-memory arm for this load.
  }
  return { index, variant: String(index), source: "assigned" };
}

// Publishes the resolved arm: sets `window.raventicVariant` (the analytics package's
// global) and pushes the single exposure event
//   { event: "RaventicABTest", raventic: { raventic_variant, raventic_experiment } }
// for EVERY arm (null arm included, so the baseline group is recorded too). One copy per
// consumer:
//   - always to `window.dataLayer` (created if absent) — GTM / GA. With analytics'
//     `useDataLayer` on (default) DataLayerCollector beacons every dataLayer entry, so this
//     copy also reaches Raventic;
//   - with `useDataLayer: false` the engine doesn't read dataLayer, so the same object ALSO
//     goes to `window.raventicLayer` (created if absent; what `window.RaventicAnalytics(e)`
//     does), which RaventicLayerCollector always beacons.
// Never both layers while useDataLayer is on (Raventic would receive it twice). With
// analytics off / absent: dataLayer only. (The arm itself reaches Raventic in every case
// through the analytics init event's `variant`; this is about the experiment id.) Each layer
// gets its OWN object: GTM mutates what is pushed to dataLayer (`gtm.uniqueEventId`), which
// must not leak into the copy Raventic beacons.
// Returns the pushed entries, one per layer (for keepAbEvent()).
export function pushAbEvent(
  variant: string,
  experimentId: string,
  analytics: AnalyticsActivation | false | undefined,
): AbPush {
  const w = window as unknown as AbWindow;
  w.raventicVariant = variant;
  const event = (): AbEvent => ({
    event: AB_EVENT_NAME,
    raventic: { raventic_variant: variant, raventic_experiment: experimentId },
  });
  const pushed: AbPush = { dataLayer: event() };
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push(pushed.dataLayer);
  if (analytics && analytics.useDataLayer === false) {
    pushed.raventicLayer = event();
    w.raventicLayer = w.raventicLayer || [];
    w.raventicLayer.push(pushed.raventicLayer);
  }
  return pushed;
}

export interface AbEvent {
  event: string;
  raventic: { raventic_variant: string; raventic_experiment: string };
}

// What pushAbEvent() pushed: its own entry object per layer (`raventicLayer` only with
// analytics' `useDataLayer: false`).
export interface AbPush {
  dataLayer: AbEvent;
  raventicLayer?: AbEvent;
}

// Called at DOM readiness with pushAbEvent()'s entries: since the push happens at script
// start, a host script parsed later may have replaced `window.dataLayer` (or
// `window.raventicLayer`) with a new array (`dataLayer = [{ pageType: … }]` above the GTM
// snippet), dropping the event before GTM / the analytics collector ever saw it. Per layer it
// pushed to, re-pushes THAT layer's own entry object when the current array holds no
// equivalent entry — matched on `event` + `raventic.raventic_experiment` +
// `raventic.raventic_variant`, not identity, so an array that copied (or cloned:
// `dataLayer.map((e) => ({ ...e }))`, a JSON round-trip) the old entries over gets no
// duplicate; a layer reset to `undefined` gets a fresh `[entry]`. A layer it never pushed to is
// never touched, so no copy is ever added where pushAbEvent() put none. Leaves a non-array
// layer alone. Returns whether it re-pushed anything.
export function keepAbEvent(pushed: AbPush): boolean {
  const w = window as unknown as AbWindow;
  let repushed = keepEntry(w, "dataLayer", pushed.dataLayer);
  if (pushed.raventicLayer) repushed = keepEntry(w, "raventicLayer", pushed.raventicLayer) || repushed;
  return repushed;
}

function keepEntry(w: AbWindow, layerName: "dataLayer" | "raventicLayer", entry: AbEvent): boolean {
  const layer = w[layerName];
  if (layer === undefined || layer === null) {
    w[layerName] = [entry];
    return true;
  }
  if (!Array.isArray(layer) || layer.some((e) => sameAbEvent(e, entry))) return false;
  layer.push(entry);
  return true;
}

// `candidate` is an exposure event for the same experiment + arm as `entry` (any object with
// the same `event` / `raventic.raventic_experiment` / `raventic.raventic_variant`).
function sameAbEvent(candidate: unknown, entry: AbEvent): boolean {
  if (candidate === entry) return true;
  if (typeof candidate !== "object" || candidate === null) return false;
  const c = candidate as { event?: unknown; raventic?: unknown };
  if (c.event !== entry.event || typeof c.raventic !== "object" || c.raventic === null) return false;
  const r = c.raventic as { raventic_variant?: unknown; raventic_experiment?: unknown };
  return (
    r.raventic_variant === entry.raventic.raventic_variant &&
    r.raventic_experiment === entry.raventic.raventic_experiment
  );
}

// Publishes the arm on <html> (see AB_ATTRIBUTE / EXPERIMENT_ATTRIBUTE / ACTIVE_ATTRIBUTE).
// `variant` / `experimentId` = the recorded arm (both null for a plain config and for a broken
// experiment, which runs arm 0 unrecorded); `active` = the booted config has at least one
// widget. A null arm gets only `data-rvn-ab` + `data-rvn-experiment`, never `data-rvn-active`.
export function markArm(
  variant: string | null,
  experimentId: string | null,
  active: boolean,
  doc: Document = document,
): void {
  const html = doc.documentElement;
  if (variant !== null && experimentId !== null) {
    html.setAttribute(AB_ATTRIBUTE, variant);
    html.setAttribute(EXPERIMENT_ATTRIBUTE, experimentId);
  }
  if (active) html.setAttribute(ACTIVE_ATTRIBUTE, "");
}

// A valid arm index from its string form, else null.
function parseIndex(value: string, count: number): number | null {
  if (!INDEX_PATTERN.test(value)) return null;
  const index = Number(value);
  return index < count ? index : null;
}

// Index i with probability weights[i] / sum (weights already validated positive).
function pickWeighted(weights: readonly number[], random: () => number): number {
  const total = weights.reduce((sum, w) => sum + w, 0);
  let r = random() * total;
  for (let i = 0; i < weights.length; i++) {
    r -= weights[i];
    if (r < 0) return i;
  }
  // Floating-point edge (random() * total landing on the last boundary).
  return weights.length - 1;
}

// The page's query string; "" where `location` is unavailable (tests, odd embeds).
function currentSearch(): string {
  try {
    return window.location.search ?? "";
  } catch {
    return "";
  }
}
